import React, { useReducer } from "react";
import { IUseCryptoWallets } from "./ICryptoWallets";
import { walletFormValidation } from "@funds/features/helpers/validation";
import { Omit } from "react-redux";
import { AnyAction } from "redux";
import { setAdditionalValues } from "../WithdrawForm/useWithdrawForm";
import { IUseFormPayload } from "../WithdrawForm/IWithdrawForm";

const initialValue: Omit<IUseCryptoWallets, "dispatch"> = {
  errors: { initial: "initial error disables Next button" },
  values: { stepLoading: false, editMode: false },
};

const reducer = (
  state: Omit<IUseCryptoWallets, "dispatch">,
  action: AnyAction
) => {
  switch (action.type) {
    case "edit":
      const data = getUpdatedData(
        action.payload,
        action.relatedData || [],
        state.values
      );
      const error = action.payload.errors
        ? { address_error: action.payload.errors }
        : {};

      return {
        values: data.values,
        errors: {
          ...data.errors,
          ...walletFormValidation(
            data.values,
            data.values.currency?.value || ""
          ),
          ...error,
        },
      };

    case "editMode":
      const newValues = { ...state.values, ...action.payload };
      return {
        ...state,
        values: newValues,
        errors: {
          ...walletFormValidation(newValues, newValues.currency?.value ?? ""),
          ...initialValue.errors,
        },
      };

    case "setInitial":
      return {
        errors: initialValue.errors,
        values: { ...initialValue.values, ...action.payload },
      };

    case "setErrors":
      return { ...state, errors: action.errors };

    default:
      return state;
  }
};

const getUpdatedData = (
  payload: IUseFormPayload,
  relatedData: IUseFormPayload[],
  stateValues: IUseCryptoWallets["values"]
) => {
  const additionalValues = setAdditionalValues(payload.name, payload.value);
  const newValues = {
    ...stateValues,
    [payload.name]: payload.value,
    ...additionalValues,
    ...relatedData.reduce(
      (acc, curr, i) => ({ ...acc, [curr.name]: curr.value }),
      {}
    ),
  } as IUseCryptoWallets["values"];
  const currency = newValues.currency ? newValues.currency.value : undefined;

  const errors = walletFormValidation(newValues, currency || "");

  return {
    errors,
    values: newValues,
  };
};

export const useCryptoWallets = (): IUseCryptoWallets => {
  const [state, dispatch] = useReducer(reducer, initialValue);

  return {
    dispatch,
    values: state.values,
    errors: state.errors,
  };
};
